import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/utils/api";

export default function Quiz() {
  const { courseId, moduleIndex } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  const isFinalQuiz = moduleIndex === "final";

  useEffect(() => {
    fetchQuiz();
  }, []);

  const fetchQuiz = async () => {
    try {
      const response = await api.get(`/courses/${courseId}`);
      setCourse(response.data);

      const quizQuestions = isFinalQuiz
        ? response.data.final_quiz
        : response.data.modules[parseInt(moduleIndex)].quiz_questions;

      setQuestions(quizQuestions);
      setAnswers(new Array(quizQuestions.length).fill(null));
    } catch (error) {
      toast.error("Failed to load quiz");
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (questionIndex, optionIndex) => {
    const newAnswers = [...answers];
    newAnswers[questionIndex] = optionIndex;
    setAnswers(newAnswers);
  };

  const handleSubmit = async () => {
    // Check if all questions are answered
    if (answers.includes(null)) {
      toast.error("Please answer all questions");
      return;
    }

    try {
      const response = await api.post("/progress/quiz", {
        course_id: courseId,
        module_index: isFinalQuiz ? null : parseInt(moduleIndex),
        answers: answers,
      });

      setResult(response.data);
      setSubmitted(true);

      if (response.data.passed) {
        toast.success(`Congratulations! You passed with ${response.data.score.toFixed(0)}%`, {
          duration: 5000,
          description: isFinalQuiz ? "Your certificate is now ready!" : "You can proceed to the next module."
        });
      } else {
        toast.error(`Quiz failed. Score: ${response.data.score.toFixed(0)}%`, {
          duration: 5000,
          description: `You need ${course.passing_score}% to pass. Review the material and try again.`
        });
      }
    } catch (error) {
      toast.error("Failed to submit quiz");
    }
  };

  const handleRetry = () => {
    setAnswers(new Array(questions.length).fill(null));
    setSubmitted(false);
    setResult(null);
    toast.info("Quiz reset. Good luck!");
  };

  const handleContinue = () => {
    if (isFinalQuiz) {
      toast.success("Course completed! Redirecting to certificates...");
      navigate("/worker/certificates");
    } else {
      // Go to next module or final quiz
      const nextModuleIndex = parseInt(moduleIndex) + 1;
      if (nextModuleIndex < course.modules.length) {
        navigate(`/worker/course/${courseId}`);
      } else {
        // All modules done, go to final quiz
        toast.info("All modules completed! Time for the final quiz.");
        navigate(`/quiz/${courseId}/final`);
      }
    }
  };

  if (loading) {
    return (
      <div className="worker-ui min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#2e7fd4] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading quiz...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="worker-ui min-h-screen bg-white">
      {/* Header */}
      <div className="bg-[#0a2240] text-white px-4 py-4 sticky top-0 z-10">
        <button
          onClick={() => navigate(`/worker/course/${courseId}`)}
          className="flex items-center text-white mb-2"
          data-testid="back-button"
        >
          <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <h1 className="text-2xl font-bold mb-1">
          {isFinalQuiz ? "Final Quiz" : `Module ${parseInt(moduleIndex) + 1} Quiz`}
        </h1>
        <p className="text-slate-300 text-sm">
          {questions.length} questions • {course?.passing_score}% to pass
        </p>
      </div>

      <div className="px-4 py-6">
        {!submitted ? (
          /* Quiz Questions */
          <div className="space-y-8">
            {questions.map((question, qIndex) => (
              <div key={qIndex} className="bg-white border-2 border-slate-200 rounded-2xl p-6">
                <p className="text-lg font-bold text-slate-900 mb-4">
                  {qIndex + 1}. {question.question}
                </p>
                <div className="space-y-3">
                  {question.options.map((option, oIndex) => (
                    <button
                      key={oIndex}
                      onClick={() => handleAnswerSelect(qIndex, oIndex)}
                      className={`quiz-option w-full text-left ${
                        answers[qIndex] === oIndex ? "selected" : ""
                      }`}
                      data-testid={`quiz-option-${qIndex}-${oIndex}`}
                    >
                      <span className="font-semibold mr-3">
                        {String.fromCharCode(65 + oIndex)}.
                      </span>
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            <Button
              onClick={handleSubmit}
              disabled={answers.includes(null)}
              className="w-full thumb-button bg-[#0a2240] hover:bg-[#0f325c] text-white"
              data-testid="submit-quiz-button"
            >
              SUBMIT QUIZ
            </Button>
          </div>
        ) : (
          /* Quiz Results */
          <div className="space-y-6">
            {/* Result Card */}
            <div
              className={`border-2 rounded-2xl p-8 text-center ${
                result.passed
                  ? "bg-green-50 border-green-500"
                  : "bg-red-50 border-red-500"
              }`}
            >
              <div
                className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${
                  result.passed ? "bg-green-500" : "bg-red-500"
                }`}
              >
                {result.passed ? (
                  <svg className="w-12 h-12 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : (
                  <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </div>
              <h2 className="text-3xl font-bold mb-2">
                {result.passed ? "Quiz Passed!" : "Quiz Failed"}
              </h2>
              <p className="text-2xl font-bold mb-4">{result.score.toFixed(0)}%</p>
              <p className="text-lg">
                {result.correct_answers} out of {result.total_questions} correct
              </p>
            </div>

            {/* Review Answers */}
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-slate-900">Review Answers</h3>
              {questions.map((question, qIndex) => {
                const userAnswer = answers[qIndex];
                const correctAnswer = question.correct_answer;
                const isCorrect = userAnswer === correctAnswer;

                return (
                  <div key={qIndex} className="bg-white border-2 border-slate-200 rounded-2xl p-6">
                    <p className="text-lg font-bold text-slate-900 mb-4">
                      {qIndex + 1}. {question.question}
                    </p>
                    <div className="space-y-3">
                      {question.options.map((option, oIndex) => {
                        const isUserAnswer = userAnswer === oIndex;
                        const isCorrectOption = correctAnswer === oIndex;

                        return (
                          <div
                            key={oIndex}
                            className={`quiz-option ${
                              isCorrectOption
                                ? "correct"
                                : isUserAnswer
                                ? "incorrect"
                                : ""
                            }`}
                          >
                            <span className="font-semibold mr-3">
                              {String.fromCharCode(65 + oIndex)}.
                            </span>
                            {option}
                            {isCorrectOption && (
                              <span className="ml-2 text-green-600 font-bold">✓ Correct</span>
                            )}
                            {isUserAnswer && !isCorrectOption && (
                              <span className="ml-2 text-red-600 font-bold">✗ Your answer</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              {result.passed ? (
                <Button
                  onClick={handleContinue}
                  className="w-full thumb-button bg-[#0e9f8e] hover:bg-[#0b7d70] text-white"
                  data-testid="continue-button"
                >
                  {isFinalQuiz ? "VIEW CERTIFICATE" : "CONTINUE TO NEXT MODULE"}
                </Button>
              ) : (
                <Button
                  onClick={handleRetry}
                  className="w-full thumb-button bg-[#2e7fd4] hover:bg-[#1c64b0] text-white"
                  data-testid="retry-button"
                >
                  RETRY QUIZ
                </Button>
              )}
              <Button
                onClick={() => navigate("/worker/dashboard")}
                variant="outline"
                className="w-full h-14 text-lg font-semibold"
              >
                BACK TO DASHBOARD
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="h-24"></div>
    </div>
  );
}
